import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import type { Course } from '../../types/course';

export function useCourse(courseId?: string) {
  const [course, setCourse] = useState<Course | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!courseId) {
      setIsLoading(false);
      return;
    }

    async function loadCourse() {
      setIsLoading(true);
      setError(null);

      try {
        const { data, error } = await supabase
          .from('courses')
          .select(`
            *,
            modules:course_modules (
              *,
              lessons:course_lessons (
                *
              )
            )
          `)
          .eq('id', courseId)
          .single();

        if (error) throw error;

        // Transform the data to match the Course type
        if (data) {
          const transformedCourse = {
            ...data,
            modules: data.modules?.map(module => ({
              ...module,
              lessons: module.lessons?.sort((a, b) => a.sequence_number - b.sequence_number) || [],
              completed: false
            })).sort((a, b) => a.sequence_number - b.sequence_number) || []
          };

          setCourse(transformedCourse);
          setError(null);
        }
      } catch (err) {
        console.error('Error loading course:', err);
        setError(err instanceof Error ? err : new Error('Failed to load course'));
        setCourse(null);
        toast.error('Failed to load course');
      } finally {
        setIsLoading(false);
      }
    }

    loadCourse();
  }, [courseId]);

  return {
    course,
    isLoading,
    error
  };
}